import React from 'react';
import { cn } from '@/lib/utils';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'outline' | 'primary' | 'secondary' | 'accent';
  hover?: boolean;
}

export function Card({ 
  children, 
  className, 
  variant = 'default',
  hover = true 
}: CardProps) {
  // Card variants
  const variantStyles = {
    default: 'bg-primary-950/40 border border-white/10 text-white',
    outline: 'bg-primary-950/30 border border-white/15 text-white',
    primary: 'bg-primary-950/55 border border-white/10 text-white',
    secondary: 'bg-primary-950/30 border border-white/10 text-white',
    accent: 'bg-primary-950/45 border border-accent/25 text-white',
  };
  
  return (
    <div 
      className={cn(
        'rounded-2xl p-6 shadow-sm backdrop-blur',
        variantStyles[variant],
        hover && 'transition-all duration-200 hover:-translate-y-0.5 hover:border-accent/30 hover:bg-white/5',
        className
      )}
    >
      {children}
    </div>
  );
}

interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export function CardContent({ children, className }: CardContentProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {children}
    </div>
  );
}

interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export function CardHeader({ children, className }: CardHeaderProps) {
  return (
    <div className={cn('mb-4', className)}>
      {children}
    </div>
  );
}

interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

export function CardFooter({ children, className }: CardFooterProps) {
  return (
    <div className={cn('mt-4 flex items-center border-t border-white/10 pt-4', className)}>
      {children}
    </div>
  );
}

export function CardGrid({ 
  children, 
  className 
}: { 
  children: React.ReactNode, 
  className?: string 
}) {
  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6', className)}>
      {children}
    </div>
  );
}
